import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import { COURSES_QUERY_KEY } from '@/features/courses/useCourses';
import { courseQueryKey } from '@/features/courses/useCourse';

import { deleteComponent } from './componentService';

export interface DeleteComponentMutationInput {
  componentId: string;
  componentUid?: number | string | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

export function useDeleteComponent(sourceCourseId: string | undefined) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<void, ApiError, DeleteComponentMutationInput>({
    mutationFn: async ({ componentId, componentUid }) => {
      if (!sourceCourseId) {
        throw createDataError('Course ID is required to delete this chapter.');
      }

      if (!user) {
        throw createDataError('You must be signed in to delete a chapter.');
      }

      const userId = getKuduUserId(user);

      if (!userId) {
        throw createDataError('User ID is required to delete this chapter.');
      }

      const normalizedComponentId = componentId.trim();

      if (!normalizedComponentId) {
        throw createDataError('Component ID is required to delete this chapter.');
      }

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      await deleteComponent(client, {
        componentId: normalizedComponentId,
        componentUid,
        fromCourseId: sourceCourseId,
        userId,
      });
    },
    onSuccess: async () => {
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: COURSES_QUERY_KEY }),
      ];

      if (sourceCourseId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: courseQueryKey(sourceCourseId),
          }),
        );
      }

      await Promise.all(invalidations);
    },
  });
}
